import { Switch } from '@headlessui/react';
import { useProviderIcon } from 'components/integrations/useProviderIcon';
import { differenceInHours, formatDistanceToNow, parseISO } from 'date-fns';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import {
  useGetCustomerSegmentsQuery,
  useSyncOnceMutation,
  useUpdateSegmentMutation,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';

interface ProviderActionCardProps {
  data: {
    category: string;
    provider: string;
    created_at: string;
    connection_status: boolean;
    id: number;
    sync_frequency: string;
  };
}

export const ProviderActionCard = ({ data }: ProviderActionCardProps) => {
  const { ProviderIcon } = useProviderIcon({
    provider: data.provider.toLowerCase(),
  });

  const { rawQueryParams, serializedStoreName } = useParseURLQueryParams();

  const { currentStore } = useAppSelector(userDetailsState);

  const { data: segments, isFetching } = useGetCustomerSegmentsQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );
  const currentSegment = segments?.find(
    (segment) =>
      segment.segment_name.toLowerCase() ===
      rawQueryParams?.segment_name?.toLowerCase()
  );

  const [updateSegment, { isLoading: updateSegmentLoading }] =
    useUpdateSegmentMutation();

  const [syncOnce, { isLoading }] = useSyncOnceMutation();

  const isDataUpdating = updateSegmentLoading || isLoading || isFetching;

  const currentProvider = currentSegment?.integrations.find(
    (integration) =>
      integration.provider.toLowerCase() === data.provider.toLowerCase()
  );

  const lastSynced = currentProvider?.last_synced
    ? parseISO(currentProvider?.last_synced)
    : null;

  const autoSync = Boolean(currentProvider?.auto_synced);

  const handleToggleAutoSync = () => {
    const body = {
      auto_synced: String(!!!currentProvider?.auto_synced),
      integration_id: currentProvider?.id,
    };

    updateSegment({
      body,
      segmentId: Number(currentSegment?.id),
      serializedStoreName: serializedStoreName,
    })
      .unwrap()
      .then(() => {
        toast.success(
          autoSync
            ? 'Segment sync has been disabled successfully'
            : 'Segment sync has been scheduled successfully'
        );
      });
  };

  const handleSyncOnce = () => {
    const rawParams = {
      store_name: rawQueryParams?.store_name,
      segment_id: String(currentSegment?.id),
      integration_id: String(currentProvider?.id),
    };
    syncOnce({
      provider: data.provider,
      rawParams,
    });
  };

  useEffect(() => {
    if (!!!data?.connection_status) {
      toast.error(
        `${data.provider} connection is not valid. Please reconnect it.`,
        { toastId: 'provider-connection-error' }
      );
    }
  }, [data.provider, data?.connection_status]);

  const syncFrequencyInHours: Record<string, number> = {
    daily: 24,
    weekly: 168,
  };

  const syncFrequencyDisabled = lastSynced
    ? differenceInHours(new Date(), lastSynced) <
      syncFrequencyInHours[currentProvider?.sync_frequency || 'daily']
    : false;

  const StatusMessage = () => {
    if (isDataUpdating) {
      return <span>Loading...</span>;
    }
    if (!!!data?.connection_status) {
      return (
        <span className="font-bold text-segments-danger">Connection error</span>
      );
    }

    if (autoSync) {
      return (
        <span>
          On queue for{' '}
          <span className="font-bold">
            {currentProvider?.sync_frequency || 'daily'}{' '}
          </span>{' '}
          syncing
        </span>
      );
    } else {
      return <span>Manual syncing</span>;
    }
  };

  return (
    <div className="px-10 py-5 border rounded-2xl ">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-8 space-y-0.5">
          <div>{ProviderIcon}</div>
          <div>
            <p className="font-bold">{data.provider}</p>

            <p className="text-black text-opacity-50">
              <span className="font-bold">Last sync: </span>
              {isDataUpdating
                ? 'Loading...'
                : lastSynced
                ? formatDistanceToNow(lastSynced, { addSuffix: true })
                : 'Not synced yet'}
            </p>
            <p className="text-black text-opacity-50">
              <span className="font-bold">Status: </span>

              <StatusMessage />
            </p>

            <button
              className="font-bold rounded-lg text-segments-link focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSyncOnce}
              title={
                syncFrequencyDisabled
                  ? `${currentProvider?.provider} can be synced 1x ${currentProvider?.sync_frequency}`
                  : ''
              }
              disabled={
                isDataUpdating ||
                Boolean(!!!data.connection_status) ||
                syncFrequencyDisabled ||
                autoSync
              }
            >
              Sync now
            </button>
          </div>
        </div>
        {data.connection_status ? (
          <div>
            <Switch
              aria-label="Toggle Auto sync"
              aria-checked={autoSync}
              as="button"
              checked={autoSync}
              disabled={isDataUpdating || Boolean(!!!data.connection_status)}
              onChange={handleToggleAutoSync}
              className={`bg-gray-200 relative inline-flex flex-shrink-0 h-10 transition-colors duration-200 ease-in-out rounded-full cursor-pointer w-36 focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed items-center`}
            >
              {({ checked }) => (
                <span
                  className={`${
                    checked
                      ? 'translate-x-6 bg-segments-accent-color-1 text-white'
                      : 'translate-x-0  text-segments-accent-color-1 bg-white'
                  }  px-4 h-full transition duration-200 ease-in-out transform font-bold rounded-full flex items-center justify-center text-segments-13px border border-segments-accent-color-1`}
                >
                  Auto Sync {checked ? 'On' : 'Off'}
                </span>
              )}
            </Switch>
          </div>
        ) : (
          <Link
            to={{
              pathname: '/integrations',
              search: new URLSearchParams({
                store_name: rawQueryParams.store_name,
                reconnect: data.provider,
              }).toString(),
            }}
            className="px-10 py-2.5 text-white bg-segments-accent-color-1 rounded-xl font-bold"
          >
            Reconnect
          </Link>
        )}
      </div>
    </div>
  );
};
